import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import api from '../api/axiosConfig';
import './LoginPage.css';
import Footer from './Footer';

const LoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({
    email: '',
    senha: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Login method: 'senha' (email + password) or 'cpf' (CPF + email code)
  const [loginMethod, setLoginMethod] = useState('senha');
  const [cpf, setCpf] = useState('');
  const [codigo, setCodigo] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [cpfMessage, setCpfMessage] = useState('');

  const handleChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/api/login', { ...loginData, rememberMe });
      
      if (response.data.success) {
        // Save token and user info to localStorage
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.aplicador));
        
        setMessage(t('login.successMessage'));
        
        // Redirect to cadastro page after short delay
        setTimeout(() => {
          navigate('/cadastro');
        }, 1000);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        setMessage('❌ ' + error.response.data.message);
      } else {
        setMessage(t('login.errorMessage'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setSendingCode(true);
    setCpfMessage('');

    try {
      const response = await api.post('/api/login-cpf/solicitar-codigo', { cpf });
      setCpfMessage(response.data.message);
      setCodeSent(true);
    } catch (error) {
      const msg = error.response?.data?.message || t('login.errorMessage');
      setCpfMessage('❌ ' + msg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerifyingCode(true);
    setCpfMessage('');

    try {
      const response = await api.post('/api/login-cpf/verificar-codigo', { cpf, codigo, rememberMe });

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.aplicador));

        setCpfMessage(t('login.successMessage'));

        setTimeout(() => {
          navigate('/cadastro');
        }, 1000);
      }
    } catch (error) {
      const msg = error.response?.data?.message || t('login.errorMessage');
      setCpfMessage('❌ ' + msg);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleChangeCpf = () => {
    setCodeSent(false);
    setCodigo('');
    setCpfMessage('');
  };

  return (
    <div className="login-container">
      <div className="login-header">
        <button onClick={() => navigate('/')} className="back-btn">{t('common.back')}</button>
        <Link to="/" className="logo-link">
          <div className="logo-wrapper-small">
            <div className="logo-circle-small">
              <img src="/logo.png" alt="IGL Coatings Logo" />
            </div>
          </div>
        </Link>
        <h1>{t('login.title')}</h1>
        <p>{t('login.subtitle')}</p>
      </div>

      <div className="login-content">
        <div className="login-method-toggle">
          <button
            type="button"
            className={`login-method-btn ${loginMethod === 'senha' ? 'active' : ''}`}
            onClick={() => setLoginMethod('senha')}
          >
            {t('login.methodPassword')}
          </button>
          <button
            type="button"
            className={`login-method-btn ${loginMethod === 'cpf' ? 'active' : ''}`}
            onClick={() => setLoginMethod('cpf')}
          >
            {t('login.methodCpf')}
          </button>
        </div>

        {loginMethod === 'senha' ? (
          <>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label>{t('login.email')} *</label>
                <input
                  type="email"
                  name="email"
                  value={loginData.email}
                  onChange={handleChange}
                  placeholder={t('login.emailPlaceholder')}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{t('login.password')} *</label>
                <input
                  type="password"
                  name="senha"
                  value={loginData.senha}
                  onChange={handleChange}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                />
              </div>

              <div className="remember-me">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  {t('login.rememberMe')}
                </label>
              </div>

              {message && (
                <div className="message">
                  {message}
                </div>
              )}

              <button type="submit" className="login-btn" disabled={loading}>
              {loading ? t('login.submitting') : t('login.submit')}
            </button>
            </form>

            <div className="forgot-password-link">
              <button 
                type="button" 
                className="forgot-password-btn" 
                onClick={() => navigate('/esqueci-senha')}
              >
                {t('login.forgotPassword')}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={codeSent ? handleVerifyCode : handleSendCode} className="login-form">
            <div className="form-group">
              <label>{t('login.cpfLabel')} *</label>
              <input
                type="text"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                placeholder={t('login.cpfPlaceholder')}
                readOnly={codeSent}
                className={codeSent ? 'input-locked' : ''}
                required
              />
            </div>

            {codeSent && (
              <>
                <div className="form-group">
                  <label>{t('login.codeLabel')} *</label>
                  <input
                    type="text"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('login.codePlaceholder')}
                    maxLength={6}
                    required
                    autoFocus
                  />
                  <span className="cpf-code-hint">{t('login.codeHint')}</span>
                </div>

                <div className="remember-me">
                  <label>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    {t('login.rememberMe')}
                  </label>
                </div>
              </>
            )}

            {cpfMessage && (
              <div className="message">
                {cpfMessage}
              </div>
            )}

            <button type="submit" className="login-btn" disabled={codeSent ? verifyingCode : sendingCode}>
              {codeSent
                ? (verifyingCode ? t('login.verifyingCode') : t('login.verifyCode'))
                : (sendingCode ? t('login.sendingCode') : t('login.sendCode'))}
            </button>

            {codeSent && (
              <div className="forgot-password-link">
                <button type="button" className="forgot-password-btn" onClick={handleChangeCpf}>
                  {t('login.changeCpf')}
                </button>
                <button type="button" className="forgot-password-btn" onClick={handleSendCode} disabled={sendingCode}>
                  {t('login.resendCode')}
                </button>
              </div>
            )}
          </form>
        )}

      </div>
      
      <Footer />
    </div>
  );
};

export default LoginPage;
